import { useTranslate } from "ra-core";
import { useNavigate, useSearchParams } from "react-router";
import { AlertTriangle, Inbox, Loader2, RefreshCw } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { contactWhatsAppSectionPath } from "../../contacts/contactDeepLinks";
import { EmptyHint } from "../../echoflicks/EchoflicksSessionList";
import type { InboxConversation, InboxState } from "../../types";
import { InboxTable } from "./InboxTable";
import {
  INBOX_STATES,
  formatRelativeTime,
  isInboxState,
  summaryCount,
} from "./inboxModel";
import {
  inboxErrorMessage,
  useInboxCapability,
  useInboxConversations,
  useInboxSummary,
  useInboxTransition,
  useNow,
  type InboxTransitionAction,
} from "./useInbox";

const STATE_PARAM = "state";

/** Customer messages inbox: contacts whose last WhatsApp message Felix could
 *  not handle, triaged through needs reply → claimed → resolved. */
export const WhatsAppInboxPage = () => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`crm.whatsapp.inbox.${key}`, options);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stateParam = searchParams.get(STATE_PARAM);
  const state: InboxState = isInboxState(stateParam)
    ? stateParam
    : "needs_reply";
  const capability = useInboxCapability();
  const summary = useInboxSummary();
  const list = useInboxConversations(state);
  const transition = useInboxTransition();
  const now = useNow();

  const selectState = (next: string) => {
    if (!isInboxState(next)) return;
    const params = new URLSearchParams(searchParams);
    params.set(STATE_PARAM, next);
    setSearchParams(params, { replace: true });
  };

  const openChat = (row: InboxConversation) => {
    void navigate(contactWhatsAppSectionPath(row.contact_id));
  };

  const runTransition = (
    row: InboxConversation,
    action: InboxTransitionAction,
  ) => {
    if (action === "release") {
      void transition.release(row);
      return;
    }
    void transition.run({ action, contactId: row.contact_id });
  };

  const refresh = () => {
    void summary.refetch();
    void list.refetch();
  };

  const oldest = summary.data?.oldest_needs_reply_since ?? null;
  const refreshing = summary.isFetching || list.isFetching;

  return (
    <div className="mx-auto max-w-7xl space-y-4 py-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Inbox className="size-5 text-primary" />
                {t("title")}
              </CardTitle>
              <CardDescription>{t("description")}</CardDescription>
            </div>
            {capability.enabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
                {translate("ra.action.refresh")}
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!capability.resolved ? (
            <EmptyHint>{t("loading")}</EmptyHint>
          ) : capability.error || !capability.enabled ? (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertTitle>{t("capability.unavailable")}</AlertTitle>
              <AlertDescription>
                {capability.error || t("capability.disabled")}
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Tabs value={state} onValueChange={selectState}>
                <TabsList>
                  {INBOX_STATES.map((tab) => {
                    const count = summaryCount(summary.data, tab);
                    return (
                      <TabsTrigger key={tab} value={tab} className="gap-2">
                        {t(`tabs.${tab}`)}
                        {count !== null ? (
                          <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
                            {count}
                          </span>
                        ) : null}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </Tabs>

              {list.isError ? (
                <Alert variant="destructive">
                  <AlertTitle>{t("error")}</AlertTitle>
                  <AlertDescription>
                    {inboxErrorMessage(list.error)}
                  </AlertDescription>
                </Alert>
              ) : list.isPending ? (
                <EmptyHint>{t("loading")}</EmptyHint>
              ) : list.rows.length === 0 ? (
                <EmptyHint>{t(`empty.${state}`)}</EmptyHint>
              ) : (
                <InboxTable
                  rows={list.rows}
                  state={state}
                  now={now}
                  onOpen={openChat}
                  onTransition={runTransition}
                  isPending={(contactId) => transition.isPendingFor(contactId)}
                />
              )}

              {list.hasNextPage ? (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void list.fetchNextPage()}
                    disabled={list.isFetchingNextPage}
                  >
                    {list.isFetchingNextPage ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : null}
                    {t("load_more")}
                  </Button>
                </div>
              ) : null}

              {list.data ? (
                <p className="text-xs text-muted-foreground">
                  {t("showing", {
                    count: list.rows.length,
                    total: list.total ?? list.rows.length,
                  })}
                  {state === "needs_reply" && oldest
                    ? ` · ${t("oldest_waiting", {
                        time: formatRelativeTime(oldest, now),
                      })}`
                    : ""}
                </p>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
